import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, FileUp, Plus, Trash2 } from 'lucide-react';
import { Badge, Button, Input, Select, Spinner } from '@/components/ui';
import {
  downloadPolicyPdf,
  getPolicyVersion,
  previewPolicyVersion,
  returnPolicyVersion,
  runPolicyVersionCommand,
  savePolicyDraft,
  uploadPolicyPdf,
  type AcknowledgementMode,
  type DraftInput,
  type PolicyBlock,
  type PolicyBlockKind,
  type PolicyContext,
  type PolicyVersion,
  type PolicyVersionCommand,
} from '@/api/services/hrPolicies.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import {
  ACKNOWLEDGEMENT_MODE_LABELS,
  formatPolicyDate,
  fromDateInput,
  POLICY_VERSION_STATUS,
  toDateInput,
} from './policyFormat';

const TEXTAREA_CLASS =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

const BLOCK_KINDS: Array<{ value: PolicyBlockKind; label: string }> = [
  { value: 'heading', label: 'Heading' },
  { value: 'paragraph', label: 'Paragraph' },
  { value: 'bullets', label: 'Bullet list' },
  { value: 'spacer', label: 'Space' },
];

const MODE_OPTIONS = (Object.keys(ACKNOWLEDGEMENT_MODE_LABELS) as AcknowledgementMode[]).map(
  (value) => ({ value, label: ACKNOWLEDGEMENT_MODE_LABELS[value] })
);

interface Draft {
  title: string;
  contentKind: 'blocks' | 'pdf';
  blocks: PolicyBlock[];
  summaryOfChanges: string;
  mode: AcknowledgementMode;
  windowDays: number;
  requiresReacknowledgement: boolean;
  effectiveDate: string;
}

function toDraft(version: PolicyVersion, timeZone: string): Draft {
  return {
    title: version.title,
    contentKind: version.contentKind,
    blocks: (version.blocks ?? []).map((block) => ({ ...block })),
    summaryOfChanges: version.summaryOfChanges ?? '',
    mode: version.acknowledgement.mode,
    windowDays: version.acknowledgement.windowDays,
    requiresReacknowledgement: version.requiresReacknowledgement,
    effectiveDate: toDateInput(version.effectiveAt, timeZone),
  };
}

function toInput(draft: Draft, version: PolicyVersion, timeZone: string): DraftInput {
  const unchangedDate = draft.effectiveDate === toDateInput(version.effectiveAt, timeZone);
  return {
    title: draft.title.trim(),
    contentKind: draft.contentKind,
    blocks: draft.blocks,
    summaryOfChanges: draft.summaryOfChanges,
    acknowledgement: { mode: draft.mode, windowDays: draft.windowDays },
    requiresReacknowledgement: draft.requiresReacknowledgement,
    ...(!unchangedDate && draft.effectiveDate && {
      effectiveAt: fromDateInput(draft.effectiveDate, timeZone),
    }),
  };
}

/** A fetched PDF as an object URL, revoked when it changes. */
function usePolicyPdf(context: PolicyContext, version: PolicyVersion | undefined) {
  const hasFile = Boolean(version?.artifact ?? version?.file);
  const { data } = useQuery({
    queryKey: ['hr-policy-pdf', context.companyId ?? 'organization', version?._id, version?.updatedAt],
    queryFn: () => downloadPolicyPdf(context, version!._id),
    enabled: hasFile,
    staleTime: Infinity,
  });
  const url = useMemo(() => (data ? URL.createObjectURL(data) : null), [data]);
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url]
  );
  return url;
}

/**
 * One policy version: a draft is edited here, a submitted one approved or
 * returned by someone other than its author, and an approved one published.
 * The server enforces every rule; this only avoids offering what it refuses.
 */
export function PolicyVersionPanel({
  context,
  versionId,
  editable,
  onChanged,
}: {
  context: PolicyContext;
  versionId: string;
  editable: boolean;
  onChanged: () => void;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [edits, setEdits] = useState<{ key: string; draft: Draft } | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [returning, setReturning] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const { data: version, isLoading } = useQuery({
    queryKey: ['hr-policy-version', context.companyId ?? 'organization', versionId],
    queryFn: () => getPolicyVersion(context, versionId),
  });
  const versionKey = version ? `${version._id}:${version.updatedAt ?? ''}:${version.status}` : '';
  const timeZone = context.timeZone ?? 'UTC';
  const draft = version ? (edits?.key === versionKey ? edits.draft : toDraft(version, timeZone)) : null;
  const canEdit = editable && version?.status === 'draft';
  const pdfUrl = usePolicyPdf(context, version);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['hr-policy-version'] });
    void queryClient.invalidateQueries({ queryKey: ['hr-policy-pdf'] });
    onChanged();
  };
  const fail = (error: unknown): void => setFormError(getErrorMessage(error));
  const update = (next: Partial<Draft>): void => {
    if (draft) setEdits({ key: versionKey, draft: { ...draft, ...next } });
  };
  const updateBlock = (index: number, next: Partial<PolicyBlock>): void =>
    update({
      blocks: draft!.blocks.map((block, at) => (at === index ? { ...block, ...next } : block)),
    });
  const moveBlock = (index: number, delta: number): void => {
    const blocks = [...draft!.blocks];
    const target = index + delta;
    if (target < 0 || target >= blocks.length) return;
    [blocks[index], blocks[target]] = [blocks[target], blocks[index]];
    update({ blocks });
  };

  const save = useMutation({
    mutationFn: () => savePolicyDraft(context, versionId, toInput(draft!, version!, timeZone)),
    onSuccess: () => {
      toast.success('Draft saved');
      setEdits(null);
      refresh();
    },
    onError: fail,
  });
  const upload = useMutation({
    mutationFn: (file: File) => uploadPolicyPdf(context, versionId, file),
    onSuccess: () => {
      toast.success('PDF uploaded');
      refresh();
    },
    onError: fail,
  });
  const command = useMutation({
    mutationFn: async (name: PolicyVersionCommand) => {
      if (name === 'submit') await savePolicyDraft(context, versionId, toInput(draft!, version!, timeZone));
      await runPolicyVersionCommand(context, versionId, name);
      return name;
    },
    onSuccess: (name) => {
      const scheduled = version && new Date(version.effectiveAt) > new Date();
      toast.success(
        {
          submit: 'Sent for approval',
          approve: 'Approved. Publish it when ready.',
          publish: scheduled
            ? `Scheduled for ${formatPolicyDate(version!.effectiveAt, timeZone)}`
            : 'Published. Employees in the audience have been asked to acknowledge it.',
          discard: 'Draft discarded',
        }[name]
      );
      setEdits(null);
      refresh();
    },
    onError: fail,
  });
  const giveBack = useMutation({
    mutationFn: () => returnPolicyVersion(context, versionId, returnReason.trim()),
    onSuccess: () => {
      toast.success('Returned to draft');
      setReturning(false);
      setReturnReason('');
      refresh();
    },
    onError: fail,
  });
  const preview = useMutation({
    mutationFn: async () => {
      if (canEdit) await savePolicyDraft(context, versionId, toInput(draft!, version!, timeZone));
      return previewPolicyVersion(context, versionId);
    },
    onSuccess: setPreviewHtml,
    onError: fail,
  });

  if (isLoading || !version || !draft) {
    return (
      <div className="flex justify-center py-10" role="status">
        <Spinner />
        <span className="sr-only">Loading version</span>
      </div>
    );
  }

  const status = POLICY_VERSION_STATUS[version.status];
  const busy =
    save.isPending || command.isPending || upload.isPending || preview.isPending || giveBack.isPending;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={status.variant}>{status.label}</Badge>
        <span className="text-sm text-gray-600 dark:text-gray-300">
          Version {version.version} · effective {formatPolicyDate(version.effectiveAt, timeZone)}
        </span>
      </div>

      <Input
        id="policy-title"
        label="Title shown to employees"
        value={draft.title}
        disabled={!canEdit}
        onChange={(event) => update({ title: event.target.value })}
      />

      <fieldset className="space-y-3">
        <legend className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
          Content
        </legend>
        <div
          role="radiogroup"
          aria-label="Content source"
          className="flex flex-wrap gap-4 text-sm text-gray-700 dark:text-gray-200"
        >
          {(
            [
              ['blocks', 'Write it here'],
              ['pdf', 'Upload a PDF'],
            ] as const
          ).map(([value, label]) => (
            <label key={value} className="flex items-center gap-2">
              <input
                type="radio"
                name="policy-content-kind"
                className="h-4 w-4 border-gray-300 text-primary focus:ring-primary"
                checked={draft.contentKind === value}
                disabled={!canEdit}
                onChange={() => update({ contentKind: value })}
              />
              {label}
            </label>
          ))}
        </div>

        {draft.contentKind === 'pdf' ? (
          <div className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <p className="text-sm text-gray-700 dark:text-gray-200">
              {version.file
                ? `${version.file.originalName ?? 'Policy PDF'} · ${Math.ceil(version.file.size / 1024)} KB`
                : 'No PDF uploaded yet.'}
            </p>
            {canEdit && (
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-primary">
                <FileUp className="h-4 w-4" aria-hidden />
                {version.file ? 'Replace PDF' : 'Choose PDF'}
                <input
                  type="file"
                  accept="application/pdf"
                  className="sr-only"
                  disabled={busy}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) upload.mutate(file);
                  }}
                />
              </label>
            )}
            {upload.isPending && (
              <p className="text-xs text-gray-600 dark:text-gray-300" role="status">
                Uploading…
              </p>
            )}
          </div>
        ) : (
          <>
            {draft.blocks.length === 0 && (
              <p className="text-sm text-gray-600 dark:text-gray-300">No content yet.</p>
            )}
            {draft.blocks.map((block, index) => (
              <div
                key={index}
                className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700"
              >
                <div className="flex flex-wrap items-end gap-2">
                  <div className="w-40">
                    <Select
                      clearable={false}
                      label={`Block ${index + 1}`}
                      value={block.kind}
                      disabled={!canEdit}
                      onChange={(value) =>
                        updateBlock(index, {
                          kind: value as PolicyBlockKind,
                          ...(value === 'bullets' && !block.items && { items: [''] }),
                        })
                      }
                      options={BLOCK_KINDS}
                    />
                  </div>
                  {canEdit && (
                    <div className="ml-auto flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Move block ${index + 1} up`}
                        disabled={index === 0}
                        onClick={() => moveBlock(index, -1)}
                      >
                        <ArrowUp className="h-4 w-4" aria-hidden />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Move block ${index + 1} down`}
                        disabled={index === draft.blocks.length - 1}
                        onClick={() => moveBlock(index, 1)}
                      >
                        <ArrowDown className="h-4 w-4" aria-hidden />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove block ${index + 1}`}
                        onClick={() =>
                          update({ blocks: draft.blocks.filter((_, at) => at !== index) })
                        }
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </Button>
                    </div>
                  )}
                </div>
                {(block.kind === 'heading' || block.kind === 'paragraph') && (
                  <textarea
                    aria-label={`Block ${index + 1} text`}
                    rows={block.kind === 'heading' ? 1 : 4}
                    value={block.text ?? ''}
                    disabled={!canEdit}
                    onChange={(event) => updateBlock(index, { text: event.target.value })}
                    className={TEXTAREA_CLASS}
                  />
                )}
                {block.kind === 'bullets' && (
                  <div className="space-y-2">
                    {(block.items ?? []).map((item, itemIndex) => (
                      <div key={itemIndex} className="flex gap-2">
                        <input
                          aria-label={`Block ${index + 1} item ${itemIndex + 1}`}
                          value={item}
                          disabled={!canEdit}
                          onChange={(event) => {
                            const items = [...(block.items ?? [])];
                            items[itemIndex] = event.target.value;
                            updateBlock(index, { items });
                          }}
                          className={TEXTAREA_CLASS}
                        />
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Remove item ${itemIndex + 1}`}
                            onClick={() =>
                              updateBlock(index, {
                                items: (block.items ?? []).filter((_, at) => at !== itemIndex),
                              })
                            }
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </Button>
                        )}
                      </div>
                    ))}
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => updateBlock(index, { items: [...(block.items ?? []), ''] })}
                      >
                        <Plus className="mr-1 h-4 w-4" aria-hidden />
                        Add item
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {canEdit && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => update({ blocks: [...draft.blocks, { kind: 'paragraph', text: '' }] })}
              >
                <Plus className="mr-1 h-4 w-4" aria-hidden />
                Add block
              </Button>
            )}
          </>
        )}
      </fieldset>

      <fieldset className="grid gap-4 sm:grid-cols-2">
        <legend className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
          Acknowledgement
        </legend>
        <div className="sm:col-span-2">
          <Select
            clearable={false}
            label="How employees respond"
            value={draft.mode}
            disabled={!canEdit}
            onChange={(value) => update({ mode: value as AcknowledgementMode })}
            options={MODE_OPTIONS}
          />
        </div>
        <Input
          id="policy-effective"
          type="date"
          label="Effective from"
          value={draft.effectiveDate}
          disabled={!canEdit}
          onChange={(event) => update({ effectiveDate: event.target.value })}
        />
        {draft.mode !== 'none' && (
          <Input
            id="policy-window"
            type="number"
            label="Days to acknowledge"
            min={1}
            max={365}
            value={String(draft.windowDays)}
            disabled={!canEdit}
            onChange={(event) =>
              update({ windowDays: Math.min(365, Math.max(1, Number(event.target.value) || 1)) })
            }
          />
        )}
        {draft.mode !== 'none' && (
          <p className="text-xs text-gray-600 dark:text-gray-300 sm:col-span-2">
            Each employee has this many days from the effective date, or from when they join the
            audience if later.
          </p>
        )}
        {version.version > 1 && (
          <>
            <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200 sm:col-span-2">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                checked={draft.requiresReacknowledgement}
                disabled={!canEdit}
                onChange={(event) => update({ requiresReacknowledgement: event.target.checked })}
              />
              <span>
                Everyone must acknowledge again. Leave this off for a minor correction: people who
                acknowledged the previous version stay covered.
              </span>
            </label>
            <div className="sm:col-span-2">
              <label
                htmlFor="policy-changes"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
              >
                What changed
              </label>
              <textarea
                id="policy-changes"
                rows={2}
                value={draft.summaryOfChanges}
                disabled={!canEdit}
                onChange={(event) => update({ summaryOfChanges: event.target.value })}
                className={TEXTAREA_CLASS}
              />
            </div>
          </>
        )}
      </fieldset>

      {previewHtml && draft.contentKind === 'blocks' && (
        <section aria-label="Preview">
          <iframe
            title="Policy preview"
            srcDoc={previewHtml}
            sandbox=""
            className="h-[60vh] w-full rounded-lg border border-gray-200 bg-white dark:border-gray-700"
          />
        </section>
      )}
      {pdfUrl && !canEdit && (
        <section aria-label="Published document">
          <iframe
            title={`${version.title} version ${version.version}`}
            src={pdfUrl}
            className="h-[60vh] w-full rounded-lg border border-gray-200 dark:border-gray-700"
          />
          {version.artifact && (
            <p className="mt-1 break-all font-mono text-[11px] text-gray-500 dark:text-gray-400">
              SHA-256 {version.artifact.sha256}
            </p>
          )}
        </section>
      )}

      {returning && (
        <form
          className="space-y-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (returnReason.trim().length >= 3) giveBack.mutate();
          }}
        >
          <Input
            id="policy-return-reason"
            label="What needs to change?"
            value={returnReason}
            onChange={(event) => setReturnReason(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setReturning(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={returnReason.trim().length < 3}
              loading={giveBack.isPending}
            >
              Return to draft
            </Button>
          </div>
        </form>
      )}

      {formError && (
        <p className="text-sm text-danger" role="alert">
          {formError}
        </p>
      )}

      {editable && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
          {draft.contentKind === 'blocks' ? (
            <Button variant="ghost" size="sm" onClick={() => preview.mutate()} loading={preview.isPending}>
              Preview
            </Button>
          ) : (
            <span />
          )}
          <div className="flex flex-wrap gap-2">
            {version.status === 'draft' && (
              <>
                {version.version > 1 && (
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => command.mutate('discard')}>
                    Discard draft
                  </Button>
                )}
                <Button variant="secondary" size="sm" loading={save.isPending} disabled={busy} onClick={() => save.mutate()}>
                  Save draft
                </Button>
                <Button size="sm" disabled={busy} onClick={() => command.mutate('submit')}>
                  Send for approval
                </Button>
              </>
            )}
            {version.status === 'pending_approval' && !returning && (
              <>
                <Button variant="secondary" size="sm" disabled={busy} onClick={() => setReturning(true)}>
                  Return to draft
                </Button>
                <Button size="sm" loading={command.isPending} disabled={busy} onClick={() => command.mutate('approve')}>
                  Approve
                </Button>
              </>
            )}
            {version.status === 'approved' && (
              <Button size="sm" loading={command.isPending} disabled={busy} onClick={() => command.mutate('publish')}>
                {new Date(version.effectiveAt) > new Date() ? 'Schedule publication' : 'Publish now'}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
